/**
 * Pure helpers of the CopyField component (issue #108): what is shown on
 * screen versus what is copied. No DOM here, so they are unit tested in the
 * node environment like the rest of lib/.
 */
import { TOKEN_MASK } from "./token-commands";

/** Visible characters of a truncated value, ellipsis included. */
export const DEFAULT_MAX_CHARS = 48;

/** Keeps the start (scheme, host) and the end (routing key) of a long value; the middle becomes an ellipsis. */
export function truncateMiddle(value: string, maxChars: number = DEFAULT_MAX_CHARS): string {
    const chars = Array.from(value);
    if (maxChars < 3 || chars.length <= maxChars) return value;
    const keep = maxChars - 1;
    const head = Math.ceil(keep * 0.6);
    const tail = keep - head;
    return chars.slice(0, head).join("") + "…" + chars.slice(chars.length - tail).join("");
}

const URL_PARTS = /^([a-z][a-z0-9+.-]*:\/\/[^/?#]*)(.*)$/is;

/** Fewest characters the path of a truncated URL keeps, whatever the budget: enough to tell two routing keys apart. */
export const MIN_URL_PATH_CHARS = 12;

/**
 * Truncates a URL without ever hiding where it points: the scheme, the host and the port stay intact, and only the
 * path (the routing key of a public endpoint) is shortened in the middle. A text that is not a URL is truncated
 * like any other. The result can be longer than `maxChars` when the origin alone is.
 */
export function truncateUrl(value: string, maxChars: number = DEFAULT_MAX_CHARS): string {
    const parts = URL_PARTS.exec(value);
    if (!parts) return truncateMiddle(value, maxChars);
    const origin = parts[1] as string;
    const rest = parts[2] as string;
    if (Array.from(value).length <= maxChars) return value;
    const budget = Math.max(maxChars - Array.from(origin).length, MIN_URL_PATH_CHARS);
    return origin + truncateMiddle(rest, budget);
}

/** Shortens a shown text for a narrow place: a URL keeps its origin, anything else loses its middle. */
export function truncateForDisplay(value: string, maxChars: number = DEFAULT_MAX_CHARS): string {
    return URL_PARTS.test(value) ? truncateUrl(value, maxChars) : truncateMiddle(value, maxChars);
}

/**
 * The text a CopyField puts on screen.
 * `display` (already masked by the caller, e.g. a command embedding a masked token) wins over `masked`,
 * which shows the fixed-length mask. The clipboard always gets the full `value`, never this text.
 */
export function shownText({ value, display, masked }: { value: string; display?: string; masked?: boolean }): string {
    if (display !== undefined) return display;
    return masked ? TOKEN_MASK : value;
}

/** The shown text, truncated (URLs keep their origin) unless `truncate` is off or the caller asked to see it in full. */
export function visibleText(
    shown: string,
    { truncate, maxChars, expanded }: { truncate: boolean; maxChars?: number; expanded?: boolean },
): string {
    if (!truncate || expanded) return shown;
    return truncateForDisplay(shown, maxChars);
}

/** Is there anything to expand? Used to offer "Show full" only when truncation hides characters. */
export function isTruncated(shown: string, truncate: boolean, maxChars: number = DEFAULT_MAX_CHARS): boolean {
    return truncate && visibleText(shown, { truncate, maxChars }) !== shown;
}
